import { format } from 'date-fns';
import { getPhoneNumberValue } from '@/components/FormFieldPhone';
import { combinedDateTime, formatDateOnly, parseHeightToDecimalFeet } from '@/lib/utils';
import { mapHiAttemptTenantsToPayload } from '@/pages/homeInspection/hiAttemptHelpers';
import type {
  HiAttemptCreatePayload,
  HiAttemptUpdatePayload,
} from '@/pages/homeInspection/hiAttempt.schema';
import {
  ATTEMPT_TYPE,
  type AttemptDetails,
  type WorkOrderAttemptCreateRequest,
  type WorkOrderAttemptMedia,
  type WorkOrderAttemptUpdateRequest,
} from '@/types/workOrderAttempt.types';
import type { PsFirstAttemptHiFormValues } from './psFirstAttemptHi.schema';

const toOptionalString = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

type PsServiceFieldPayload = Partial<{
  personAccepting: string | null;
  relation: string | null;
  age: number | null;
  height: number | null;
  weight: number | null;
  hair: string | null;
  sex: string | null;
  race: string | null;
  otherDetails: string | null;
  emailAddress: string | null;
  emailSentDate: Date | null;
  emailAcknowledgementDate: Date | null;
  remarks: string | null;
  trackingNumber: string | null;
}>;

/** Only include service fields allowed for the attempt type (API rejects disallowed keys on update). */
export const pickPsAttemptServiceFields = (attempt: AttemptDetails): PsServiceFieldPayload => {
  const type = attempt.type;
  if (type === ATTEMPT_TYPE.PERSONAL || type === ATTEMPT_TYPE.SUB) {
    return {
      personAccepting: attempt.personAccepting ?? null,
      relation: attempt.relation ?? null,
      age: attempt.age ? Number(attempt.age) : null,
        height: parseHeightToDecimalFeet(attempt.height),
      weight: attempt.weight ? Number(attempt.weight) : null,
      hair: attempt.hair ?? null,
      sex: attempt.sex ?? null,
      race: attempt.race ?? null,
      otherDetails: attempt.otherDetails ?? null,
    };
  }
  if (type === ATTEMPT_TYPE.BY_EMAIL) {
    return {
      emailAddress: attempt.emailAddress ?? null,
      emailSentDate: attempt.emailSentDate ?? null,
      emailAcknowledgementDate: attempt.emailAcknowledgementDate ?? null,
      remarks: attempt.remarks ?? null,
    };
  }
  if (type === ATTEMPT_TYPE.BY_MAIL) {
    return {
      trackingNumber: attempt.trackingNumber ?? null,
    };
  }
  // ATTEMPTED / NOT_SERVED / POSTED / unset — no type-specific service fields
  return {};
};

export type LinkedHomeInspectionAttemptPayload = Omit<
  HiAttemptCreatePayload,
  'dateOfAttempt' | 'timeOfAttempt'
>;

export const mapPsFirstAttemptToLinkedHiPayload = (
  values: PsFirstAttemptHiFormValues,
  options: { includeVacancyChecklist: boolean; hiMediaIds?: string[] }
): LinkedHomeInspectionAttemptPayload => {
  const full = mapPsFirstAttemptToHiCreatePayload(values, options);
  const { dateOfAttempt, timeOfAttempt, ...linked } = full;
  void dateOfAttempt;
  void timeOfAttempt;
  return linked;
};

export const mapPsFirstAttemptToPsCreatePayload = (
  values: PsFirstAttemptHiFormValues,
  mediaIds: string[],
  options?: { homeInspectionAttempt?: LinkedHomeInspectionAttemptPayload }
): WorkOrderAttemptCreateRequest => {
  const attempt = values.serviceAttempt as AttemptDetails;
  return {
    type: attempt.type,
    description: attempt.description ?? null,
    additionalInformation: attempt.additionalInformation ?? null,
    ...pickPsAttemptServiceFields(attempt),
    dateOfAttempt: combinedDateTime(attempt.dateOfAttempt ?? undefined, attempt.timeOfAttempt ?? undefined),
    mediaIds,
    ...(options?.homeInspectionAttempt
      ? { homeInspectionAttempt: options.homeInspectionAttempt }
      : {}),
  };
};

/** Map form values to a PS attempt update payload (media create/delete deltas). */
export const mapPsFirstAttemptToPsUpdatePayload = (
  values: PsFirstAttemptHiFormValues,
  options: {
    attemptId: string;
    originalMedia?: WorkOrderAttemptMedia[] | null;
    currentMediaIds: string[];
  }
): WorkOrderAttemptUpdateRequest => {
  const attempt = values.serviceAttempt as AttemptDetails;
  const original = options.originalMedia ?? [];
  const currentIds = new Set(options.currentMediaIds);
  const originalIds = new Set(original.map((m) => m.mediaId));

  return {
    attemptId: options.attemptId,
    type: attempt.type,
    description: attempt.description ?? null,
    ...pickPsAttemptServiceFields(attempt),
    dateOfAttempt: combinedDateTime(attempt.dateOfAttempt ?? undefined, attempt.timeOfAttempt ?? undefined),
    mediaIds: {
      create: options.currentMediaIds.filter((id) => !originalIds.has(id)),
      delete: original.filter((m) => !currentIds.has(m.mediaId)).map((m) => m.attemptMediaId),
    },
    additionalInformation: attempt.additionalInformation ?? null,
  };
};

/** Full HI update body (date/time from PS service step). Includes vacancy when provided. */
export const mapPsFirstAttemptToHiUpdatePayload = (
  values: PsFirstAttemptHiFormValues,
  options: { includeVacancyChecklist: boolean; hiMediaIds?: string[] }
): HiAttemptUpdatePayload => mapPsFirstAttemptToHiCreatePayload(values, options);

export const mapPsFirstAttemptToHiCreatePayload = (
  values: PsFirstAttemptHiFormValues,
  options: { includeVacancyChecklist: boolean; hiMediaIds?: string[] }
): HiAttemptCreatePayload => {
  const attempt = values.serviceAttempt as AttemptDetails;
  const date = attempt.dateOfAttempt ?? new Date();
  const time = attempt.timeOfAttempt ?? new Date();
  const timeOfAttempt = format(time instanceof Date ? time : new Date(time), 'HH:mm');

  const payload: HiAttemptCreatePayload = {
    dateOfAttempt: date instanceof Date ? formatDateOnly(date) : String(date),
    timeOfAttempt,
    ownerAddressLine1: values.ownerAddressLine1 ?? null,
    ownerAddressLine2: values.ownerAddressLine2 ?? null,
    ownerCity: values.ownerCity ?? null,
    ownerState: values.ownerState ?? null,
    ownerZipCode: values.ownerZipCode ?? null,
    ownerPhone: getPhoneNumberValue(values.ownerPhone) ?? null,
    maritalStatus: values.maritalStatus ?? null,
    spouseName: values.maritalStatus === 'MARRIED' ? (values.spouseName ?? null) : null,
    ownerOccupied: values.ownerOccupied ?? null,
    vacant: values.vacant ?? null,
    bothOwnersResideTogether: values.bothOwnersResideTogether ?? null,
    coOwnerName: values.coOwnerName ?? null,
    coOwnerAddressLine1: values.coOwnerAddressLine1 ?? null,
    coOwnerAddressLine2: values.coOwnerAddressLine2 ?? null,
    coOwnerCity: values.coOwnerCity ?? null,
    coOwnerState: values.coOwnerState ?? null,
    coOwnerZipCode: values.coOwnerZipCode ?? null,
    defendantInArmedForces: values.defendantInArmedForces ?? null,
    tenantOccupied: values.tenantOccupied ?? null,
    tenants: mapHiAttemptTenantsToPayload(values.tenants),
    relative: values.tenants?.[0]?.relative ?? null,
    lease: values.tenants?.[0]?.lease ?? null,
    rentAmount: values.tenants?.[0]?.rentAmount != null ? Number(values.tenants[0].rentAmount) : null,
    houseExterior: values.houseExterior,
    houseExteriorOther:
      values.houseExterior === 'OTHER' ? toOptionalString(values.houseExteriorOther ?? undefined) : undefined,
    houseType: values.houseType,
    numberOfUnits: values.numberOfUnits != null ? Number(values.numberOfUnits) : 1,
    garage: values.garage,
    sale: values.sale,
    saleBy: values.sale === 'YES' ? toOptionalString(values.saleBy ?? undefined) : undefined,
    salePhone: values.sale === 'YES' ? (getPhoneNumberValue(values.salePhone) ?? undefined) : undefined,
    comments: values.comments ?? undefined,
    mediaIds: options.hiMediaIds ?? values.mediaIds ?? [],
  };

  if (options.includeVacancyChecklist && values.vacancyChecklist) {
    payload.vacancyChecklist = values.vacancyChecklist;
  }

  return payload;
};
